import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, MessageCircle, Send } from 'lucide-react';
import { businessInfo } from '../data/mock';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock submission
    alert('Hvala na poruci! Kontaktiraćemo vas uskoro.');
    setFormData({ name: '', phone: '', email: '', message: '' });
  };

  const handleWhatsApp = () => {
    window.open(`https://wa.me/${businessInfo.phoneRaw}`, '_blank');
  };

  const handleViber = () => {
    window.open(`viber://chat?number=${businessInfo.phoneRaw}`, '_blank');
  };

  const handleCall = () => {
    window.location.href = `tel:${businessInfo.phoneRaw}`;
  };

  return (
    <section id="kontakt" className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Kontaktirajte nas
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-teal-500 to-blue-500 mx-auto mb-6"></div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Pozovite, pošaljite poruku ili posetite našu radionicu. Tu smo da odgovorimo 
            na sva vaša pitanja.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left Column - Contact Info & Quick Actions */}
          <div className="space-y-8">
            {/* Quick Contact Actions */}
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                Kontaktirajte nas odmah
              </h3>
              
              <div className="space-y-4">
                <button
                  onClick={handleCall}
                  className="group w-full flex items-center space-x-4 bg-teal-600 text-white p-4 rounded-xl hover:bg-teal-700 transition-all shadow-lg hover:shadow-xl"
                >
                  <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                    <Phone className="w-6 h-6 group-hover:rotate-12 transition-transform" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-semibold">Pozovite nas</p>
                    <p className="text-sm opacity-90">{businessInfo.phone}</p>
                  </div>
                </button>

                <button
                  onClick={handleWhatsApp}
                  className="group w-full flex items-center space-x-4 bg-green-600 text-white p-4 rounded-xl hover:bg-green-700 transition-all shadow-lg hover:shadow-xl"
                >
                  <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                    <MessageCircle className="w-6 h-6 group-hover:scale-110 transition-transform" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-semibold">WhatsApp poruka</p>
                    <p className="text-sm opacity-90">Pošaljite nam poruku</p>
                  </div>
                </button>

                <button
                  onClick={handleViber}
                  className="group w-full flex items-center space-x-4 bg-purple-600 text-white p-4 rounded-xl hover:bg-purple-700 transition-all shadow-lg hover:shadow-xl"
                >
                  <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                    <Send className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-semibold">Viber poruka</p>
                    <p className="text-sm opacity-90">Kontaktirajte nas</p>
                  </div>
                </button>
              </div>
            </div>

            {/* Contact Information */}
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 space-y-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                Informacije
              </h3>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-teal-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900 mb-1">Adresa</p>
                  <p className="text-gray-600">{businessInfo.address.street}</p>
                  <p className="text-gray-600">{businessInfo.address.city}</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Clock className="w-6 h-6 text-teal-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900 mb-1">Radno vreme</p>
                  <p className="text-gray-600">{businessInfo.workingHours.weekdays}</p>
                  <p className="text-gray-600">{businessInfo.workingHours.weekend}</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6 text-cyan-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900 mb-1">Email</p>
                  <a 
                    href={`mailto:${businessInfo.email}`}
                    className="text-blue-600 hover:text-blue-700 transition-colors"
                  >
                    {businessInfo.email}
                  </a>
                </div>
              </div>
            </div>

            {/* Map */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
              <iframe
                title="Lokacija Obućar Tašner Laki"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2830.7573847204376!2d20.4919207!3d44.8013889!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x475a7ab41c3c6b3f%3A0x4567890abcdef!2sBulevar%20kralja%20Aleksandra%20194%2C%20Beograd!5e0!3m2!1sen!2srs!4v1234567890"
                width="100%"
                height="300"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* Right Column - Contact Form */}
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">
              Pošaljite upit
            </h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                  Ime i prezime *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                  placeholder="Vaše ime i prezime"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                  Telefon *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                  placeholder="Vaš broj telefona"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                  placeholder="vaš@email.com"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                  Poruka *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="5"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all resize-none"
                  placeholder="Opišite šta vas interesuje..."
                />
              </div>

              <button
                type="submit"
                className="w-full bg-teal-600 text-white py-4 rounded-lg font-semibold hover:bg-teal-700 transition-all shadow-lg hover:shadow-xl"
              >
                Pošalji poruku
              </button>

              <p className="text-sm text-gray-500 text-center">
                * Obavezna polja
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
